import { useMemo, useState } from "react"
import { adaptiveFractionDigits, formatFixedNumber, formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01, clampNumber } from "atlas_forecasting_app/lib/math"
import { approximateQuantileFromCdf } from "atlas_forecasting_app/forecasting/forecast_utils"
import { useContainerWidth } from "atlas_forecasting_app/hooks/use_container_width"
import { ChartTooltip } from "atlas_forecasting_app/components/charts/chart_tooltip"

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function numericAnnotationLayout(quantiles, xFromValue, chartLeft, chartRight, innerH) {
  if (!quantiles) return null

  const hasInterval = Number.isFinite(quantiles.p10) && Number.isFinite(quantiles.p90)
  const hasMedian = Number.isFinite(quantiles.p50)
  const intervalLabelWidth = 94
  const medianLabelWidth = 64
  const intervalCenterX = hasInterval ? (xFromValue(quantiles.p10) + xFromValue(quantiles.p90)) / 2 : null
  const clampedIntervalCenter = hasInterval
    ? clamp(intervalCenterX, chartLeft + intervalLabelWidth / 2, chartRight - intervalLabelWidth / 2)
    : null

  return {
    intervalX1: hasInterval ? xFromValue(quantiles.p10) : null,
    intervalX2: hasInterval ? xFromValue(quantiles.p90) : null,
    intervalLabelX: clampedIntervalCenter,
    intervalBracketY: Math.max(118, innerH - 26),
    intervalLabelY: Math.max(106, innerH - 38),
    intervalLabelLeft: hasInterval ? clampedIntervalCenter - intervalLabelWidth / 2 : null,
    intervalLabelRight: hasInterval ? clampedIntervalCenter + intervalLabelWidth / 2 : null,
    intervalLabelTop: Math.max(106, innerH - 38) - 11,
    intervalLabelBottom: Math.max(106, innerH - 38) + 7,
    medianX: hasMedian ? xFromValue(quantiles.p50) : null,
    medianLabelX: hasMedian ? clamp(xFromValue(quantiles.p50), chartLeft + medianLabelWidth / 2, chartRight - medianLabelWidth / 2) : null,
    medianLabelY: 12,
    medianCutsIntervalLabel:
      hasInterval && hasMedian
        ? xFromValue(quantiles.p50) >= clampedIntervalCenter - intervalLabelWidth / 2 &&
          xFromValue(quantiles.p50) <= clampedIntervalCenter + intervalLabelWidth / 2
        : false,
  }
}

export function NumericCdfChart({ distribution, question, mode = "pmf", height }) {
  const [ref, width] = useContainerWidth()
  const [hoverIdx, setHoverIdx] = useState(null)
  const [tooltip, setTooltip] = useState(null)
  const gradId = useMemo(() => `af_numeric_${Math.random().toString(16).slice(2)}`, [])

  const margin = { top: 16, right: 16, bottom: 34, left: 52 }
  const computedHeight = height || 240
  const innerW = Math.max(10, (width || 0) - margin.left - margin.right)
  const innerH = Math.max(10, computedHeight - margin.top - margin.bottom)

  const lower = Number(question.lower_bound)
  const upper = Number(question.upper_bound)
  const scaleType = question.scale_type || "linear"
  const validLog = scaleType === "logarithmic" && lower > 0 && upper > 0

  const pmf = distribution?.pmf
  const cdf = distribution?.cdf
  const activeSeries = mode === "cmf" ? cdf : pmf

  const xFromValue = (xVal) => {
    if (!Number.isFinite(lower) || !Number.isFinite(upper) || lower === upper) return margin.left
    if (validLog) {
      const logL = Math.log(lower)
      const logU = Math.log(upper)
      return margin.left + innerW * ((Math.log(xVal) - logL) / (logU - logL))
    }
    return margin.left + innerW * ((xVal - lower) / (upper - lower))
  }

  const valueFromX = (xPx) => {
    if (!Number.isFinite(lower) || !Number.isFinite(upper)) return lower
    const t = clampNumber((xPx - margin.left) / innerW, 0, 1)
    if (validLog) {
      const logL = Math.log(lower)
      const logU = Math.log(upper)
      return Math.exp(logL + t * (logU - logL))
    }
    return lower + t * (upper - lower)
  }

  const points = useMemo(() => {
    if (!Array.isArray(activeSeries) || activeSeries.length !== 201 || !Number.isFinite(lower) || !Number.isFinite(upper)) return null

    const incrementLinear = (upper - lower) / 200.0
    const incrementLog = validLog ? Math.pow(upper / lower, 1.0 / 200.0) : null

    return activeSeries.map((value, idx) => {
      const yVal = clamp01(Number(value)) ?? 0
      const xVal = validLog ? lower * Math.pow(incrementLog, idx) : lower + incrementLinear * idx
      const bucketMin =
        idx === 0
          ? null
          : validLog
            ? lower * Math.pow(incrementLog, idx - 1)
            : lower + incrementLinear * (idx - 1)

      return { idx, xVal, bucketMin, yVal }
    })
  }, [activeSeries, lower, upper, validLog])

  const yScaleMax = useMemo(() => {
    if (mode === "cmf") return 1
    const peak = Math.max(0.01, ...(points || []).map((point) => point.yVal))
    return Math.min(1, peak * 1.15)
  }, [mode, points])

  const yForValue = (value) => margin.top + innerH * (1 - value / yScaleMax)

  const pathD = useMemo(() => {
    if (!points || points.length === 0) return ""
    return points
      .map((point, idx) => {
        const x = xFromValue(point.xVal)
        const y = yForValue(point.yVal)
        return `${idx === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`
      })
      .join(" ")
  }, [points, yScaleMax, innerW, innerH, lower, upper, validLog])

  const areaD = useMemo(() => {
    if (!points || points.length === 0) return ""
    const first = points[0]
    const last = points[points.length - 1]
    const x0 = xFromValue(first.xVal)
    const xN = xFromValue(last.xVal)
    const yBottom = margin.top + innerH
    return `${pathD} L ${xN.toFixed(2)} ${yBottom.toFixed(2)} L ${x0.toFixed(2)} ${yBottom.toFixed(2)} Z`
  }, [points, pathD, innerW, innerH])

  const quantiles = useMemo(() => {
    if (!Array.isArray(cdf)) return null
    return {
      p10: approximateQuantileFromCdf(cdf, 0.1, question),
      p50: approximateQuantileFromCdf(cdf, 0.5, question),
      p90: approximateQuantileFromCdf(cdf, 0.9, question),
    }
  }, [cdf, question])

  const annotationLayout = useMemo(
    () => numericAnnotationLayout(quantiles, xFromValue, margin.left, margin.left + innerW, innerH),
    [quantiles, innerW, innerH, lower, upper, validLog],
  )

  const xTicks = useMemo(() => {
    if (!Number.isFinite(lower) || !Number.isFinite(upper)) return []
    const tickPositions = [0, 0.25, 0.5, 0.75, 1]
    if (validLog) {
      const ratio = upper / lower
      return tickPositions.map((position) => lower * Math.pow(ratio, position))
    }
    return tickPositions.map((position) => lower + (upper - lower) * position)
  }, [lower, upper, validLog])

  const tooltipFractionDigits = useMemo(() => adaptiveFractionDigits((points || []).map((point) => point.xVal)), [points])
  const tickFractionDigits = useMemo(() => adaptiveFractionDigits(xTicks), [xTicks])
  const formatTooltipValue = (value) => formatFixedNumber(value, tooltipFractionDigits)
  const formatTickValue = (value) => formatFixedNumber(value, tickFractionDigits)

  const onMove = (event) => {
    if (!width || !points) return
    const rect = event.currentTarget.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    if (x < margin.left || x > margin.left + innerW || y < margin.top || y > margin.top + innerH) {
      setHoverIdx(null)
      setTooltip(null)
      return
    }

    const value = valueFromX(x)
    let idx
    if (validLog) {
      idx = Math.round((Math.log(value / lower) / Math.log(upper / lower)) * 200.0)
    } else {
      idx = Math.round(((value - lower) / (upper - lower)) * 200.0)
    }
    idx = clampNumber(idx, 0, 200)

    const point = points[idx]
    const title =
      mode === "cmf"
        ? `≤ ${formatTooltipValue(point.xVal)}`
        : point.idx === 0
          ? `< ${formatTooltipValue(lower)}`
          : point.idx === 1
            ? `≥ ${formatTooltipValue(lower)} and ≤ ${formatTooltipValue(point.xVal)}`
            : `> ${formatTooltipValue(point.bucketMin)} and ≤ ${formatTooltipValue(point.xVal)}`
    const body = mode === "cmf" ? `CMF: ${formatPercent(point.yVal, 2)}` : `Probability: ${formatPercent(point.yVal, 2)}`

    setHoverIdx(idx)
    setTooltip({ x, y, title, body })
  }

  const hoverPoint = points && hoverIdx != null ? points[hoverIdx] : null

  return (
    <div ref={ref} className="af-chart" style={{ height: `${computedHeight}px` }}>
      {width > 0 && points ? (
        <svg
          width={width}
          height={computedHeight}
          onMouseMove={onMove}
          onMouseLeave={() => {
            setHoverIdx(null)
            setTooltip(null)
          }}
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={mode === "cmf" ? "rgba(34, 139, 230, 0.18)" : "rgba(34, 139, 230, 0.30)"} />
              <stop offset="100%" stopColor={mode === "cmf" ? "rgba(34, 139, 230, 0.03)" : "rgba(34, 139, 230, 0.06)"} />
            </linearGradient>
          </defs>

          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const value = yScaleMax * t
            const y = yForValue(value)
            return (
              <g key={t}>
                <line x1={margin.left} y1={y} x2={margin.left + innerW} y2={y} className={t === 0 || t === 1 ? "af-chart-grid" : "af-chart-grid-soft"} />
                <text x={margin.left - 8} y={y + 4} textAnchor="end" className="af-chart-axis-muted">
                  {formatPercent(value, mode === "cmf" ? 0 : 1)}
                </text>
              </g>
            )
          })}

          {xTicks.map((xVal, idx) => {
            const x = xFromValue(xVal)
            return (
              <g key={idx}>
                <line x1={x} y1={margin.top + innerH} x2={x} y2={margin.top + innerH + 6} className="af-chart-grid-soft" />
                <text x={x} y={computedHeight - 10} textAnchor="middle" className="af-chart-axis-muted">
                  {formatTickValue(xVal)}
                </text>
              </g>
            )
          })}

          <path d={areaD} fill={`url(#${gradId})`} />
          <path d={pathD} className="af-chart-line" />

          {quantiles?.p10 != null && quantiles?.p90 != null ? (
            <g>
              <rect
                x={annotationLayout.intervalX1}
                y={margin.top}
                width={Math.max(0, annotationLayout.intervalX2 - annotationLayout.intervalX1)}
                height={innerH}
                fill="rgba(18, 184, 134, 0.08)"
              />
              <line x1={annotationLayout.intervalX1} y1={margin.top} x2={annotationLayout.intervalX1} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.55)" strokeDasharray="4 4" />
              <line x1={annotationLayout.intervalX2} y1={margin.top} x2={annotationLayout.intervalX2} y2={margin.top + innerH} stroke="rgba(134, 142, 150, 0.55)" strokeDasharray="4 4" />
              <line
                x1={annotationLayout.intervalX1}
                y1={margin.top + annotationLayout.intervalBracketY}
                x2={annotationLayout.intervalX2}
                y2={margin.top + annotationLayout.intervalBracketY}
                stroke="rgba(18, 184, 134, 0.7)"
                strokeWidth="1.5"
              />
              <line
                x1={annotationLayout.intervalX1}
                y1={margin.top + annotationLayout.intervalBracketY - 5}
                x2={annotationLayout.intervalX1}
                y2={margin.top + annotationLayout.intervalBracketY + 5}
                stroke="rgba(18, 184, 134, 0.7)"
                strokeWidth="1.5"
              />
              <line
                x1={annotationLayout.intervalX2}
                y1={margin.top + annotationLayout.intervalBracketY - 5}
                x2={annotationLayout.intervalX2}
                y2={margin.top + annotationLayout.intervalBracketY + 5}
                stroke="rgba(18, 184, 134, 0.7)"
                strokeWidth="1.5"
              />
              <rect x={annotationLayout.intervalLabelX - 47} y={margin.top + annotationLayout.intervalLabelY - 11} width="94" height="18" rx="9" fill="rgba(248, 249, 250, 0.94)" stroke="rgba(18, 184, 134, 0.35)" />
              <text
                x={annotationLayout.intervalLabelX}
                y={margin.top + annotationLayout.intervalLabelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="af-chart-axis"
                fill="rgba(8, 127, 91, 0.96)"
                style={{ fontWeight: 600 }}
              >
                80% interval
              </text>
            </g>
          ) : null}

          {quantiles?.p50 != null ? (
            <g>
              {quantiles?.p10 != null && quantiles?.p90 != null && annotationLayout.medianCutsIntervalLabel ? (
                <g>
                  <line x1={annotationLayout.medianX} y1={margin.top} x2={annotationLayout.medianX} y2={margin.top + annotationLayout.intervalLabelTop - 3} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
                  <line x1={annotationLayout.medianX} y1={margin.top + annotationLayout.intervalLabelBottom + 3} x2={annotationLayout.medianX} y2={margin.top + innerH} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
                </g>
              ) : (
                <line x1={annotationLayout.medianX} y1={margin.top} x2={annotationLayout.medianX} y2={margin.top + innerH} stroke="rgba(18, 184, 134, 0.65)" strokeWidth="2" />
              )}
              <rect x={annotationLayout.medianLabelX - 32} y={margin.top + annotationLayout.medianLabelY - 11} width="64" height="18" rx="9" fill="rgba(235, 251, 238, 0.96)" stroke="rgba(18, 184, 134, 0.45)" />
              <text
                x={annotationLayout.medianLabelX}
                y={margin.top + annotationLayout.medianLabelY}
                textAnchor="middle"
                dominantBaseline="middle"
                className="af-chart-axis"
                fill="rgba(8, 127, 91, 0.96)"
                style={{ fontWeight: 700 }}
              >
                Median
              </text>
            </g>
          ) : null}

          {hoverPoint ? (
            <g>
              <line x1={xFromValue(hoverPoint.xVal)} y1={margin.top} x2={xFromValue(hoverPoint.xVal)} y2={margin.top + innerH} className="af-chart-grid" />
              <circle cx={xFromValue(hoverPoint.xVal)} cy={yForValue(hoverPoint.yVal)} r="5.2" className="af-chart-dot" />
            </g>
          ) : null}
        </svg>
      ) : null}
      <ChartTooltip tooltip={tooltip} containerWidth={width} containerHeight={computedHeight} />
    </div>
  )
}
